// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
import { Check } from "lucide-react";

import type {
  PayrollWorkflowStage,
  PayrollWorkflowStageId,
} from "../../routes/payrollWorkflow";
import { payrollStageStateLabel } from "../../routes/payrollWorkflow";

/**
 * Schlanke horizontale Workflow-Leiste (Variante A): nur Nummer + Kurztitel.
 * Statusdetails (Completed/Blocked) und Blockergründe stehen im Workspace-Inhalt,
 * nicht in der Leiste, um Überladung zu vermeiden.
 */
export const PayrollWorkflowRail = ({
  stages,
  selectedId,
  recommendedId,
  onSelect,
}: {
  stages: PayrollWorkflowStage[];
  selectedId: PayrollWorkflowStageId;
  recommendedId: PayrollWorkflowStageId;
  onSelect: (id: PayrollWorkflowStageId) => void;
}) => (
  <nav
    aria-label="Monthly payroll workflow"
    className="min-w-0 px-4"
  >
    <ol className="flex flex-wrap items-center gap-1.5 text-xs font-medium">
      {stages.map((stage, index) => {
        const selected = stage.id === selectedId;
        const recommended = stage.id === recommendedId;
        const completed = stage.state === "completed";

        const tone = selected
          ? "border-foreground bg-foreground text-surface"
          : completed
            ? "border-transparent bg-accent-subtle text-accent"
            : "border-border bg-surface text-foreground-subtle";

        const clickable = stage.interactive;

        const content = (
          <>
            <span className="font-semibold tabular-nums">{index + 1}</span>
            <span>{{ prepare: "Prepare", pohoda: "POHODA", payslips: "Payslips", reconcile: "Balances", close: "Close" }[stage.id]}</span>
            {completed && <Check size={13} aria-hidden="true" className="shrink-0" />}
          </>
        );

        const className = `inline-flex min-h-11 items-center gap-1 rounded-lg border px-2 py-1 text-left text-xs font-medium outline-none transition-colors ${tone} ${
          clickable
            ? "cursor-pointer hover:opacity-85 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
            : "cursor-default"
        }`;

        return (
          <li key={stage.id} data-workflow-stage={stage.id}>
            {clickable ? (
              <button
                type="button"
                onClick={() => onSelect(stage.id)}
                aria-current={selected ? "step" : undefined}
                aria-label={`Step ${index + 1}, ${stage.title}: ${payrollStageStateLabel(stage.state)}${recommended && stage.interactive ? ", current stage" : ""}`}
                style={selected ? { color: "var(--color-surface)" } : undefined}
                className={className}
              >
                {content}
              </button>
            ) : (
              <div
                role="group"
                aria-disabled="true"
                aria-label={`Step ${index + 1}, ${stage.title}: ${payrollStageStateLabel(stage.state)}${stage.state === "blocked" ? `. ${stage.blocker ?? "Complete the previous stage first."}` : ""}`}
                title={stage.state === "blocked" ? stage.blocker : undefined}
                className={className}
              >
                {content}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  </nav>
);
